<template>
  <v-container fluid>
    <UniversalTable
      :config="tableConfig.tableParams.value"
      :items="filteredPayRanges"
      @onActionClick="viewState.openCreatePayRangeModal()"
      @onSearch="viewState.state.filters.searchText = $event"
      @onClick="viewState.state.filters.showFilterDialog = true"
      @removeFilter="viewState.removeFilters"
      :emptyObjectData="emptyObjectData"
      @removeAllFilters="viewState.clearFilters"
      :draggable="true"
      @onDrag="onDrag"
    >
      <template #Dropdown-activator>
        <div class="icon-container">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="icon-svg"
          >
            <path
              d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </template>
    </UniversalTable>
    <PayRangeModal
      :open="viewState.state.showPayRangeModal.show"
      :mode="viewState.state.showPayRangeModal.mode"
      :payRangeId="viewState.state.showPayRangeModal.payRangeId"
      :stepNumber="viewState.state.showPayRangeModal.stepNumber"
      :initialValues="modalInitialValues"
      :saveDraft="handleSaveDraft"
      :suggestRange="handleSuggestRange"
      @onClose="viewState.closePayRangeModal"
      @closeAddDialog="viewState.closePayRangeModal"
    />
    <SearchModal
      :dialog="viewState.state.filters.showFilterDialog"
      @back="viewState.state.filters.showFilterDialog = false"
      @checkedItemsChanged="viewState.applyFilters"
      :clearSelection="viewState.state.filters.clearSelection"
      filterType="Employee"
      :filterGroups="viewState.state.filters.filterGroups"
      :statusOptions="viewState.state.filters.statusOptions"
      :modalWidth="800"
      :defaultSelected="viewState.state.filters.values"
    />
  </v-container>
</template>

<script setup>
import { onMounted, getCurrentInstance, computed } from "vue";
import UniversalTable from "@components/UniversalTable/UniversalTable.vue";
import { useTableConfig } from "./composables/useTableConfig";
import { usePayRangeViewState } from "./composables/usePayRangeViewState";
import PayRangeModal from "@components/PayRangeModal/PayRangeModal.vue";
import SearchModal from "@components/SearchBar/SearchModal.vue";
import { usePayRangeStore } from "@/store/pinia/payRange";
import { toModalInitialValues } from "@components/PayRangeModal/payRangeDraftPayload";

const app = getCurrentInstance();
const { proxy } = app;

const tableConfig = useTableConfig();
const viewState = usePayRangeViewState();
const payRangeStore = usePayRangeStore();

const emptyObjectData = {
  singular: "Pay Range",
  plural: "Pay Ranges",
};

const modalInitialValues = computed(() =>
  toModalInitialValues(viewState.state.showPayRangeModal.initialValues || {})
);

const handleSuggestRange = (seed, options) => payRangeStore.suggestRange(seed, options);

const handleSaveDraft = async (form) => {
  await payRangeStore.saveDraft(form);
  if (proxy?.$notifications?.success) {
    proxy.$notifications.success(form.mode === "edit" ? "Draft updated" : "Draft saved");
  }
};

const filteredPayRanges = computed(() => {
  let payRangeList = [...payRangeStore.payRanges];

  if (viewState.state.filters.searchText && viewState.state.filters.searchText.length > 0) {
    const searchText = viewState.state.filters.searchText.toLowerCase();

    payRangeList = payRangeList.filter((el) => {
      const fullName = `${el?.pay_range_header.name} ${el.pay_range_header.location}`.toLowerCase();
      return (
        fullName.includes(searchText) ||
        el?.pay_range_header.name.toLowerCase().includes(searchText) ||
        el.pay_range_header.location.toLowerCase().includes(searchText)
      );
    });
  }

  if (
    viewState.state.filters.values.some((val) =>
      viewState.state.filters.statusOptions.includes(val)
    )
  ) {
    payRangeList = payRangeList.filter((el2) =>
      viewState.state.filters.values.includes(el2.status)
    );
  }

  return payRangeList;
});

function onDrag(data) {
  const { oldIndex, newIndex } = data.moved;
  payRangeStore.reorderPayRanges(oldIndex, newIndex);
}

onMounted(async () => {
  proxy.$loader.show();
  proxy.$loader.hide();
});
</script>

<style scoped>
.icon-container {
  cursor: pointer;
}
</style>
